import { useMemo } from 'react';
import { Call } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { UserCard } from '@frontend/analytics-shared';
import { CallInsightApi, OpenParams } from '@frontend/call-insight-panel';
import {
  AiOutcomeColumn,
  AppointmentTypeColumn,
  callIntelUtils,
  CategoryColumn,
  ContactTypeColumn,
  DeviceNameColumn,
  LocationColumn,
  OfficeNumberColumn,
  OfficeUserColumn,
  SchedulingOutcomeColumn,
  SentimentColumn,
  ServiceIssueColumn,
  TaskColumn,
  TaskTypeColumn,
  TimeColumn,
} from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { PhoneOutgoingIcon, TableColumnConfig } from '@frontend/design-system';
import { trackingIds } from '../../../tracking-ids';
import { TagsColumn } from '../tables/tags-column';
import { CallIntelSubView } from './use-call-intel-store';

// Column IDs that should appear in mobile view
export const MOBILE_COLUMN_IDS = [
  'contactType',
  'sentiment',
  'schedulingOutcomeV2',
  'serviceQualityFlags',
  'category',
  'appointmentType',
  'tasks',
] as const;

// Non-analyzed columns (not classified by AI)
const NON_ANALYZED_COLUMNS: string[] = [
  'appointmentType',
  'schedulingOpportunity',
  'schedulingOutcome',
  'schedulingOutcomeV2',
  'sentiment',
  'serviceQualityFlags',
];

// Analyzed filter keys
const ANALYZED_FILTER_KEYS: (keyof CallIntelTypes.Filters)[] = [
  'callDirection',
  'categories',
  'officeNumbers',
  'officeUsers',
];

// Non-analyzed filter keys
const NON_ANALYZED_FILTER_KEYS: (keyof CallIntelTypes.Filters)[] = [
  'appointmentTypes',
  'schedulingOpportunity',
  'schedulingOutcomes',
  'sentiments',
  'serviceQualityFlags',
];

// Mapping from non-analyzed column IDs to filter keys
const NON_ANALYZED_MAPPING: Partial<Record<string, keyof CallIntelTypes.Filters>> = {
  appointmentType: 'appointmentTypes',
  schedulingOpportunity: 'schedulingOpportunity',
  schedulingOutcome: 'schedulingOutcomes',
  sentiment: 'sentiments',
};

export type CallRecordsColumnId =
  | 'person'
  | 'locationName'
  | 'time'
  | 'deviceName'
  | 'officeUserId'
  | 'aiCallOutcome'
  | 'contactType'
  | 'tasks'
  | 'tags'
  | 'followUpReason'
  | 'sentiment'
  | 'serviceQualityFlags'
  | 'schedulingOutcomeV2'
  | 'category'
  | 'appointmentType'
  | 'officeNumber';

type ColumnAccessConfig = {
  isTasksEnabled: boolean;
  isServiceQualityEnabled: boolean;
  hasServiceQualityAccess: boolean;
};

type ColumnDataConfig = {
  selectedCall?: CallIntelTypes.CallRecord | null;
  personId: string;
  isDemoModal: boolean;
  locations: Record<string, string>;
  isMultiLocation: boolean;
  activeVoiceAiLocations: string[];
  locationTaskSettingsMap?: Record<string, any>;
  officeNumbers?: string[];
  subView?: CallIntelSubView;
};

type ColumnHandlers = {
  openCallInsightPanel: CallInsightApi['open'];
  updateCallTagsInCache: (call: CallIntelTypes.CallRecord) => void;
};

type ColumnDisplayConfig = {
  drillDownFilters?: CallIntelTypes.DrillDownOptions;
};

type ColumnFilterConfig = {
  filters: CallIntelTypes.Filters;
  hideColumns: string[];
  priorityColumns: string[];
};

type UseCallRecordsColumnsParams = {
  accessConfig: ColumnAccessConfig;
  dataConfig: ColumnDataConfig;
  handlers: ColumnHandlers;
  displayConfig: ColumnDisplayConfig;
  filterConfig: ColumnFilterConfig;
};

const isFilterApplied = (value: any) => (Array.isArray(value) ? value.length > 0 : Boolean(value));

// Utility to apply column filtering based on filters and hideColumns
const applyColumnFiltering = <T extends { id: string }>(
  columns: T[],
  filters: CallIntelTypes.Filters,
  hideColumns: string[]
): T[] => {
  const contactTypesFilter = filters.contactTypes;

  const onlyNonPatient =
    Array.isArray(contactTypesFilter) &&
    contactTypesFilter.length === 1 &&
    contactTypesFilter.includes(CallIntelTypes.ContactTypeEnum.CONTACT_NOT_A_PATIENT);

  // Apply hideColumns filter
  let baseConfig: T[] = columns;
  if (hideColumns.length > 0) {
    const hideColumnsSet = new Set(hideColumns);
    baseConfig = [];
    for (const column of columns) {
      if (!hideColumnsSet.has(column.id)) {
        baseConfig.push(column);
      }
    }
  }

  // If there's no contact type filter, or if there is any other contact type filter, show all columns
  if (!onlyNonPatient) {
    return baseConfig;
  }

  // Check if any analyzed filter is applied
  const anyAnalyzedFilterApplied = ANALYZED_FILTER_KEYS.some((key) => isFilterApplied(filters[key]));

  // If any of the analyzed filters are applied, do not filter out any columns
  if (anyAnalyzedFilterApplied) {
    return baseConfig;
  }

  // Get which non-analyzed filters are applied
  const nonAnalyzedFiltersApplied = NON_ANALYZED_FILTER_KEYS.filter((key) => isFilterApplied(filters[key]));

  // If no non-analyzed filters are applied, remove all non-analyzed columns
  if (nonAnalyzedFiltersApplied.length === 0) {
    const result: T[] = [];
    for (const col of baseConfig) {
      if (NON_ANALYZED_COLUMNS.indexOf(col.id) === -1) {
        result.push(col);
      }
    }
    return result;
  }

  // For non-analyzed columns, include them only if their corresponding filter is applied
  const result: T[] = [];
  for (const col of baseConfig) {
    const columnId = col.id;
    if (NON_ANALYZED_COLUMNS.indexOf(columnId) === -1) {
      result.push(col);
    } else {
      // For non-analyzed columns
      if (columnId === 'serviceQualityFlags') {
        // Always exclude serviceQualityFlags
        continue;
      }
      const filterKey = NON_ANALYZED_MAPPING[columnId];
      if (filterKey && isFilterApplied(filters[filterKey])) {
        result.push(col);
      }
    }
  }
  return result;
};

// Utility to apply priority ordering
const applyPriorityOrdering = <T extends { id: string }>(columns: T[], priorityColumns: string[]): T[] => {
  if (priorityColumns.length === 0) {
    return columns;
  }

  return columns.sort((a, b) => {
    const aIndex = priorityColumns.indexOf(a.id);
    const bIndex = priorityColumns.indexOf(b.id);

    // If both columns are in the priorityColumns array, sort by their index
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    // If only one column is in the priorityColumns array, that one should come first
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    // If neither column is in the priorityColumns array, maintain their original order
    return 0;
  });
};

export const useCallRecordsColumns = ({
  accessConfig,
  dataConfig,
  handlers,
  displayConfig,
  filterConfig,
}: UseCallRecordsColumnsParams) => {
  const { t } = useTranslation('analytics');

  const { isTasksEnabled, isServiceQualityEnabled, hasServiceQualityAccess } = accessConfig;

  const {
    selectedCall,
    personId,
    isDemoModal,
    locations,
    isMultiLocation,
    activeVoiceAiLocations,
    locationTaskSettingsMap,
    officeNumbers,
    subView,
  } = dataConfig;

  const { openCallInsightPanel, updateCallTagsInCache } = handlers;

  const { drillDownFilters } = displayConfig;

  const { filters, hideColumns, priorityColumns } = filterConfig;

  const allTableColumns: TableColumnConfig<CallIntelTypes.CallRecord>[] = useMemo(
    () => [
      {
        accessor: (call) => call,
        cellRenderer: (value: CallIntelTypes.CallRecord) => {
          // If the contact panel already open via call insight tray, do nothing on click
          const isContactPanelOpen = selectedCall?.id === value.id && personId === value.person?.id;

          return (
            <UserCard
              firstName={value?.person?.firstName}
              lastName={value?.person?.lastName}
              preferredName={value?.person?.preferredName}
              key={value?.person?.id}
              openProfileOnClick={!isContactPanelOpen}
              phoneNumber={callIntelUtils.getPhoneNumber(value.phoneNumber)}
              openPanelInTray={isDemoModal}
              rightElement={
                value.direction === CallIntelTypes.CallDirectionEnum.DIRECTION_OUTBOUND ? (
                  <span style={{ color: theme.colors.neutral70 }}>
                    <PhoneOutgoingIcon size={16} />
                  </span>
                ) : null
              }
              showOnlyName
              userId={value?.person?.id || ''}
              locationId={value?.locationId}
            />
          );
        },
        disableColumnFilter: true,
        disableSortBy: true,
        Header: t('Contact Name'),
        id: 'person',
        sticky: 'left',
        width: 280,
      },
      {
        accessor: (call) => call,
        cellRenderer: (call) => (
          <LocationColumn.Cell callInsights={call} locationName={locations[call.locationId]} maxWidth={180} />
        ),
        disableSortBy: true,
        Header: <LocationColumn.Header />,
        id: 'locationName',
        omit: !isMultiLocation,
        width: 220,
      },
      {
        accessor: ({ startTime }) => startTime && TimeColumn.accessor(startTime),
        Header: <TimeColumn.Header />,
        id: 'time',
        startingSortBy: 'desc',
        width: 220,
      },
      {
        accessor: (call) => call,
        cellRenderer: (call) => <OfficeUserColumn.Cell callInsights={call} />,
        disableSortBy: true,
        Header: <OfficeUserColumn.Header />,
        headerLabel: <OfficeUserColumn.Header />,
        id: 'officeUserId',
        width: 200,
      },
      {
        accessor: (call) => call,
        cellRenderer: (call) => <DeviceNameColumn.Cell callInsights={call} />,
        disableSortBy: true,
        Header: <DeviceNameColumn.Header />,
        id: 'deviceName',
        width: 180,
      },
      {
        accessor: (call) => call,
        cellRenderer: (call) => <AiOutcomeColumn.Cell callInsights={call} />,
        disableSortBy: true,
        Header: <AiOutcomeColumn.Header />,
        headerLabel: <AiOutcomeColumn.HeaderLabel />,
        id: 'aiCallOutcome',
        omit: !activeVoiceAiLocations.length,
        width: 78,
      },
      {
        accessor: (call) => call,
        cellRenderer: (call) => <ContactTypeColumn.Cell callInsights={call} />,
        disableSortBy: true,
        Header: <ContactTypeColumn.Header showAiIcon={false} />,
        headerLabel: <ContactTypeColumn.HeaderLabel />,
        id: 'contactType',
        width: 240,
      },
      {
        accessor: (call) => call,
        cellRenderer: (call) => (
          <TaskColumn.Cell
            callInsights={call}
            taskCount={call.taskCount}
            trackingId={trackingIds.callIntel.taskLinkAllCallRecordings}
            withTaskChip={false}
            isAiTaskCreationEnabled={locationTaskSettingsMap?.[call.locationId]?.aiTaskCreationEnabled !== false}
            onClick={() => {
              if (call.id !== selectedCall?.id) {
                openCallInsightPanel(
                  {
                    channelId: call?.id ?? '',
                    includeCallIntelligence: false,
                    callIntelData: call,
                    locationId: call?.locationId,
                  },
                  {
                    defaultAccordion: 'tasks',
                    suppressGlobalPanelOpen: isDemoModal,
                  }
                );
              }
            }}
          />
        ),
        disableSortBy: true,
        Header: <TaskColumn.Header showAiIcon={false} />,
        headerLabel: <TaskColumn.HeaderLabel />,
        id: 'tasks',
        width: 158,
        omit: !isTasksEnabled,
      },
      {
        accessor: (row) => row,
        cellRenderer: (rowData) => {
          return (
            <TagsColumn
              call={rowData}
              onUpdateCallTags={updateCallTagsInCache}
              onOpenTagPanel={() =>
                openCallInsightPanel(
                  {
                    channelId: rowData?.id ?? '',
                    includeCallIntelligence: false,
                    callIntelData: rowData,
                    locationId: rowData?.locationId,
                  },
                  {
                    defaultAccordion: 'callIntel',
                    suppressGlobalPanelOpen: isDemoModal,
                  }
                )
              }
            />
          );
        },
        disableSortBy: true,
        Header: t('Tags'),
        id: 'tags',
        width: 180,
      },
      {
        accessor: (call) => call,
        cellRenderer: (call) => (
          <TaskTypeColumn.Cell
            callInsights={call}
            isAiTaskCreationEnabled={locationTaskSettingsMap?.[call.locationId]?.aiTaskCreationEnabled !== false}
          />
        ),
        disableSortBy: true,
        Header: <TaskTypeColumn.Header showAiIcon={false} />,
        omit: !isTasksEnabled,
        headerLabel: <TaskTypeColumn.HeaderLabel />,
        id: 'followUpReason',
        width: 281,
      },
      {
        accessor: (call) => call,
        cellRenderer: (call) => <SentimentColumn.Cell callInsights={call} />,
        disableSortBy: true,
        Header: <SentimentColumn.Header showAiIcon={false} />,
        headerLabel: <SentimentColumn.HeaderLabel />,
        id: 'sentiment',
        width: 225,
      },
      {
        accessor: (call) => call,
        cellRenderer: (call) => (
          <ServiceIssueColumn.Cell
            callInsights={call}
            overrideServiceIssues={
              drillDownFilters?.key === 'service_quality_flags'
                ? (drillDownFilters.value as CallIntelTypes.ServiceQualityFlagEnum[])
                : []
            }
            onClick={() => {
              if (call.id !== selectedCall?.id) {
                openCallInsightPanel(
                  {
                    channelId: call?.id ?? '',
                    includeCallIntelligence: false,
                    callIntelData: call,
                    locationId: call?.locationId,
                  },
                  {
                    defaultAccordion: 'callIntel',
                    defaultCallIntelTab: 'callAnalysis',
                    suppressGlobalPanelOpen: !!isDemoModal,
                  }
                );
              }
            }}
          />
        ),
        disableSortBy: true,
        Header: <ServiceIssueColumn.Header showAiIcon={false} />,
        headerLabel: <ServiceIssueColumn.HeaderLabel />,
        id: 'serviceQualityFlags',
        width: 350,
        omit: !(isServiceQualityEnabled && hasServiceQualityAccess),
      },
      {
        accessor: (call) => call,
        cellRenderer: (call) => <SchedulingOutcomeColumn.Cell callInsights={call} />,
        disableSortBy: true,
        Header: <SchedulingOutcomeColumn.Header showAiIcon={false} />,
        headerLabel: <SchedulingOutcomeColumn.HeaderLabel />,
        id: 'schedulingOutcomeV2',
        width: 250,
      },
      {
        accessor: (call) => call,
        cellRenderer: (call) => <CategoryColumn.Cell callInsights={call} />,
        disableSortBy: true,
        Header: <CategoryColumn.Header showAiIcon={false} />,
        headerLabel: <CategoryColumn.HeaderLabel />,
        id: 'category',
        width: 270,
      },
      {
        accessor: (call) => call,
        cellRenderer: (call) => <AppointmentTypeColumn.Cell callInsights={call} />,
        disableSortBy: true,
        Header: <AppointmentTypeColumn.Header showAiIcon={false} />,
        headerLabel: <AppointmentTypeColumn.HeaderLabel />,
        id: 'appointmentType',
        width: 270,
      },
      {
        accessor: (call) => OfficeNumberColumn.accessor({ callInsights: call as Call }),
        disableSortBy: true,
        Header: <OfficeNumberColumn.Header />,
        headerLabel: <OfficeNumberColumn.Header />,
        id: 'officeNumber',
        omit: (officeNumbers?.length || 0) === 1,
        width: 180,
      },
    ],
    [
      t,
      isMultiLocation,
      isTasksEnabled,
      isServiceQualityEnabled,
      hasServiceQualityAccess,
      officeNumbers?.length,
      selectedCall?.id,
      personId,
      isDemoModal,
      locations,
      locationTaskSettingsMap,
      openCallInsightPanel,
      updateCallTagsInCache,
      drillDownFilters?.key,
      drillDownFilters?.value,
      activeVoiceAiLocations,
    ]
  );

  const allSmallModeColumns = useMemo(
    () => ({
      contactType: {
        render: (call: Call) => <ContactTypeColumn.Cell callInsights={call} isSmallMode />,
        shouldRender: (call: Call) => ContactTypeColumn.shouldRender({ callInsights: call, isSmallMode: true }),
      },
      sentiment: {
        render: (call: Call) => <SentimentColumn.Cell callInsights={call} isSmallMode />,
        shouldRender: (call: Call) => SentimentColumn.shouldRender({ callInsights: call, isSmallMode: true }),
      },
      schedulingOutcomeV2: {
        render: (call: Call) => <SchedulingOutcomeColumn.Cell callInsights={call} isSmallMode />,
        shouldRender: (call: Call) => SchedulingOutcomeColumn.shouldRender({ callInsights: call, isSmallMode: true }),
      },
      serviceQualityFlags: {
        render: (call: Call) => <ServiceIssueColumn.Cell callInsights={call} isSmallMode />,
        shouldRender: (call: Call) => ServiceIssueColumn.shouldRender({ callInsights: call, isSmallMode: true }),
      },
      category: {
        render: (call: Call) => <CategoryColumn.Cell callInsights={call} isSmallMode />,
        shouldRender: (call: Call) => {
          if (subView?.type !== 'category') return false;
          return CategoryColumn.shouldRender({ callInsights: call, isSmallMode: true });
        },
      },
      appointmentType: {
        render: (call: Call) => <AppointmentTypeColumn.Cell callInsights={call} isSmallMode />,
        shouldRender: (call: Call) => {
          if (subView?.type !== 'appointment-type') return false;
          return AppointmentTypeColumn.shouldRender({ callInsights: call, isSmallMode: true });
        },
      },
      tasks: {
        render: (call: Call) => (
          <TaskColumn.Cell
            callInsights={call}
            isSmallMode
            isAiTaskCreationEnabled={
              call.locationId ? locationTaskSettingsMap?.[call.locationId]?.aiTaskCreationEnabled !== false : true
            }
            taskCount={call.taskCount}
            onClick={() => {
              if (call.id !== selectedCall?.id) {
                openCallInsightPanel(
                  {
                    channelId: call?.id ?? '',
                    includeCallIntelligence: false,
                    callIntelData: call,
                    locationId: call?.locationId,
                  } as OpenParams, //TODO: To be removed when call schema is fixed
                  {
                    defaultAccordion: 'tasks',
                    suppressGlobalPanelOpen: isDemoModal,
                  }
                );
              }
            }}
          />
        ),
        shouldRender: (call: Call) =>
          TaskColumn.shouldRender({
            callInsights: call,
            isAiTaskCreationEnabled: call.locationId
              ? locationTaskSettingsMap?.[call.locationId]?.aiTaskCreationEnabled !== false
              : true,
            isSmallMode: true,
            taskCount: call.taskCount,
          }),
      },
    }),
    [isDemoModal, locationTaskSettingsMap, openCallInsightPanel, selectedCall?.id, subView?.type]
  );

  // Apply filtering and prioritization to table columns
  const tableColumns = useMemo(() => {
    const filtered = applyColumnFiltering(allTableColumns, filters, hideColumns);
    return applyPriorityOrdering(filtered, priorityColumns);
  }, [allTableColumns, filters, hideColumns, priorityColumns]);

  // Apply filtering and prioritization to mobile columns
  const smallModeColumns = useMemo(() => {
    const mobileColumnObjects = MOBILE_COLUMN_IDS.map((id) => ({ id }));

    const filtered = applyColumnFiltering(mobileColumnObjects, filters, hideColumns);
    const prioritized = applyPriorityOrdering(filtered, priorityColumns);

    return prioritized.reduce((acc, { id }) => {
      const config = allSmallModeColumns[id];

      if (!config) return acc;

      acc[id] = (call: CallIntelTypes.CallRecord) => {
        if (config.shouldRender && !config.shouldRender(call as Call)) {
          return null;
        }
        return config.render(call as Call);
      };

      return acc;
    }, {} as Record<string, (call: CallIntelTypes.CallRecord) => React.ReactNode>);
  }, [allSmallModeColumns, filters, hideColumns, priorityColumns]);

  return {
    tableColumns,
    smallModeColumns,
  };
};
